import { InfoCircleOutlined } from '@ant-design/icons'
import { Modal, Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import { PrioritySaveBar } from '@/components/PrioritySortShared/PrioritySaveBar/PrioritySaveBar'
import { PriorityManager } from '@/components/StorePriority/ManualPriorityConfig/ManualPriorityConfig'
import {
  StorePriorityItem,
  StorePriorityRow,
} from '@/components/StorePriority/StorePriorityRow/StorePriorityRow'
import { MAX_PRIORITY } from '@/constants'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import cn from './SystemStorePrioritySort.module.scss'

type SystemStorePriorityResponse = {
  items: Array<{
    store: {
      id: string
      code: number
      description: string
      isActive: boolean
      isRemote: boolean
    }
    supplier: { id: string; description: string }
    systemPriority: number
  }>
}

type History = {
  initial: StorePriorityItem[]
  present: StorePriorityItem[]
  past: StorePriorityItem[][]
  future: StorePriorityItem[][]
}

const stateOf = (item: StorePriorityItem) => ({
  isMain: item.isMain,
  priority: item.isMain ? item.priority : undefined,
})

const SystemStorePrioritySort = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [history, setHistory] = useState<History | null>(null)
  const [modal, contextHolder] = Modal.useModal()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [priorities, context] = await Promise.all([
        api.get<SystemStorePriorityResponse>('/system/store-priorities'),
        api.get('/context'),
      ])
      const supplierId = context.data.context?.currentSupplierId ?? ''
      const items = priorities.data.items.map((item) => ({
        id: item.store.id,
        code: item.store.code,
        name: item.store.description,
        supplierName: item.supplier.description,
        isOwn: item.supplier.id === supplierId,
        isActive: item.store.isActive,
        isRemote: item.store.isRemote,
        isMain: item.systemPriority !== MAX_PRIORITY,
        priority: item.systemPriority,
        systemPriority: item.systemPriority,
      }))
      setHistory({ initial: items, present: items, past: [], future: [] })
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }, [modal])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const updateItems = useCallback((items: StorePriorityItem[]) => {
    setHistory((previous) =>
      previous
        ? {
            ...previous,
            present: items,
            past: [...previous.past, previous.present],
            future: [],
          }
        : previous
    )
  }, [])

  const undo = useCallback(() => {
    setHistory((previous) =>
      !previous?.past.length
        ? previous
        : {
            ...previous,
            present: previous.past[previous.past.length - 1],
            past: previous.past.slice(0, -1),
            future: [previous.present, ...previous.future],
          }
    )
  }, [])

  const redo = useCallback(() => {
    setHistory((previous) =>
      !previous?.future.length
        ? previous
        : {
            ...previous,
            present: previous.future[0],
            past: [...previous.past, previous.present],
            future: previous.future.slice(1),
          }
    )
  }, [])

  const changedIds = useMemo(() => {
    if (!history) return new Set<string>()
    const initial = new Map(
      history.initial.map((item) => [item.id, stateOf(item)])
    )
    return new Set(
      history.present
        .filter((item) => {
          const original = initial.get(item.id)
          const current = stateOf(item)
          return (
            !original ||
            current.isMain !== original.isMain ||
            current.priority !== original.priority
          )
        })
        .map((item) => item.id)
    )
  }, [history])

  const save = useCallback(async () => {
    if (!history || changedIds.size === 0) return
    const initial = new Map(
      history.initial.map((item) => [item.id, stateOf(item)])
    )
    const updates = Object.fromEntries(
      history.present
        .filter((item) => {
          const original = initial.get(item.id)
          const current = stateOf(item)
          return (
            !original ||
            current.isMain !== original.isMain ||
            current.priority !== original.priority
          )
        })
        .map((item) => [item.id, item.isMain ? item.priority : null])
    )
    setSaving(true)
    const loadingToast = toast.loading('Сохранение приоритетов складов...')
    try {
      await api.patch('/system/store-priorities', { updates })
      await fetchData()
      toast.success('Приоритеты складов сохранены', { id: loadingToast })
    } catch (error) {
      toast.dismiss(loadingToast)
      handleApiError(error, modal)
    } finally {
      setSaving(false)
    }
  }, [changedIds.size, fetchData, history, modal])

  return (
    <div className={cn.systemStorePriority}>
      {contextHolder}
      <Loader loaderType="modal" isModalOpen={loading} />
      {history && (
        <PriorityManager
          items={history.present}
          initialItems={history.initial}
          onDataChange={updateItems}
          onUndo={undo}
          onRedo={redo}
          canUndo={history.past.length > 0}
          canRedo={history.future.length > 0}
          mainTitle={
            <>
              Приоритет установлен{' '}
              <Tooltip title="Порядок определяет базовую выдачу складов для всех клиентов.">
                <InfoCircleOutlined />
              </Tooltip>
            </>
          }
          othersTitle="Остальные"
          mainEmptyText="Перетащите сюда склад, чтобы установить приоритет"
          othersEmptyText="Склады без установленного приоритета"
          renderRow={(props) => (
            <StorePriorityRow
              {...props}
              data={props.data}
              isChanged={changedIds.has(props.data.id)}
            />
          )}
        />
      )}
      <PrioritySaveBar
        changeCount={changedIds.size}
        onSave={save}
        onUndo={undo}
        onRedo={redo}
        canUndo={Boolean(history?.past.length)}
        canRedo={Boolean(history?.future.length)}
        loading={saving}
      />
    </div>
  )
}

export default SystemStorePrioritySort
